import { useState, useRef, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Link, useNavigate } from "react-router-dom"
import { ErrorCard } from "@/components/ui/error"
import { ROUTES } from "@/routes/path"
import { useToast } from "@/hooks/useToast"
import { useAuth, useForgotPassword, useResetPasswordConfirm } from "@/hooks/useAuth"

const OTP_LENGTH = 6
const COUNTDOWN_SECONDS = 60

export default function ResetPasswordOtp() {
  const { toast } = useToast()
  const { email } = useAuth()
  const navigate = useNavigate()

  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(""))
  const [activeIndex, setActiveIndex] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [shake, setShake] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [isResendDisabled, setIsResendDisabled] = useState(true)

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, OTP_LENGTH)
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    if (!isResendDisabled) return

    if (countdown === 0) {
      setIsResendDisabled(false)
      return
    }

    const timerId = setInterval(() => {
      setCountdown((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timerId)
  }, [countdown, isResendDisabled])

  const resendMutation = useForgotPassword({
    onSuccess: () => {
      toast({
        title: "Reset code resent",
        description: "Check your email for the new code.",
        variant: "success",
      })
      setIsResendDisabled(true)
      setCountdown(COUNTDOWN_SECONDS)
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to resend code. Please try again.",
        variant: "destructive",
      })
    },
  })

  const confirmMutation = useResetPasswordConfirm({
    onSuccess: () => {
      navigate(ROUTES.AUTH.RESET_PASSWORD_NEW)
    },
    onError: (error: any) => {
      setShake(true)
      setTimeout(() => setShake(false), 600)
      setFormError(error?.response?.data?.error || "Invalid reset code")
    },
  })

  const handleResendCode = () => {
    if (email) {
      resendMutation.mutate(email)
    }
  }

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
      setActiveIndex(index + 1)
    }

    if (newCode.every((digit) => digit !== "")) {
      // Store the token for the next step
      localStorage.setItem("resetToken", newCode.join(""))
      confirmMutation.mutate({ email: email || "", token: newCode.join("") })
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
      setActiveIndex(index - 1)
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus()
      setActiveIndex(index - 1)
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
      setActiveIndex(index + 1)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text/plain").slice(0, OTP_LENGTH)

    if (!/^\d+$/.test(pastedData)) {
      setShake(true)
      setTimeout(() => setShake(false), 600)
      return
    }

    const newCode = [...code]
    pastedData.split("").forEach((digit, index) => {
      newCode[index] = digit
    })
    setCode(newCode)

    const focusIndex = Math.min(pastedData.length, OTP_LENGTH - 1)
    inputRefs.current[focusIndex]?.focus()
    setActiveIndex(focusIndex)

    if (newCode.every((digit) => digit !== "")) {
      // Store the token for the next step
      localStorage.setItem("resetToken", newCode.join(""))
      confirmMutation.mutate({ email: email || "", token: newCode.join("") })
    }
  }

  const setInputRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el
    },
    []
  )

  if (!email) {
    navigate(ROUTES.AUTH.FORGOT_PASSWORD)
    return null
  }

  return (
    <div className="relative flex h-screen flex-col items-center bg-gray-50 dark:bg-zinc-900">
      <div className="relative z-10 flex w-full flex-col items-center">
        <div className="my-10 flex justify-center">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>

        <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-8 text-center shadow-xl dark:bg-zinc-800">
          <h1 className="text-2xl font-semibold dark:text-zinc-100">Enter reset code</h1>

          <p className="px-4 text-muted-foreground">
            If an account exists with <span className="font-semibold text-primary">{email}</span>, we&apos;ve sent a
            reset code to that email address.{" "}
            <Link to={ROUTES.AUTH.FORGOT_PASSWORD} className="text-sm text-primary underline hover:underline">
              Change email
            </Link>
          </p>

          <fieldset
            disabled={confirmMutation.isPending}
            className={cn("flex flex-col gap-4", shake && "animate-shake")}
          >
            <div className="flex items-center justify-center gap-2">
              {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                <input
                  key={`otp-input-${index}`}
                  ref={setInputRef(index)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onFocus={() => setActiveIndex(index)}
                  className={cn(
                    "size-14 text-center text-lg font-semibold",
                    "rounded-lg border",
                    "transition-all duration-200",
                    "bg-white dark:bg-zinc-900",
                    "border-zinc-200 dark:border-zinc-700",
                    "focus:outline-hidden focus:ring-2 focus:ring-primary/20",
                    activeIndex === index && "border-primary dark:border-primary"
                  )}
                />
              ))}
            </div>
            {formError && <ErrorCard message={formError} />}
          </fieldset>
          <p className="text-sm text-muted-foreground">
            Didn&apos;t receive a code?{" "}
            <Button
              variant="link"
              size="sm"
              className="px-0.5"
              onClick={handleResendCode}
              disabled={isResendDisabled || resendMutation.isPending}
            >
              {isResendDisabled
                ? `Resend in ${String(Math.floor(countdown / 60)).padStart(2, "0")}:${String(countdown % 60).padStart(2, "0")}`
                : "Resend code"}
            </Button>
          </p>
        </div>

        <div className="-mt-4 w-full max-w-md rounded-b-xl border-t bg-gray-50 px-8 py-4 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
          <Link to={ROUTES.AUTH.LOGIN}>
            Remember your password?{" "}
            <span className="font-medium text-primary hover:underline dark:text-primary">Back to login</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
